import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import { normalizeVendorKey } from "encamp-shared/src/waste/vendorDataCompleteness";
import { WasteVendorObservedDataQuery } from "generated-graphql/graphql";
import { useState } from "react";
import { prettyPrintDateFull } from "util/dates";

import {
  CREATE_WASTE_MANIFEST_VENDOR,
  CREATE_WASTE_MANIFEST_VENDOR_ALIAS,
  VENDOR_DATA_COMPLETENESS_QUERY,
  WASTE_VENDOR_OBSERVED_DATA_QUERY,
} from "./api";
import { MapVendorDialog } from "./MapVendorDialog";

export type ObservedPair = WasteVendorObservedDataQuery["wasteVendorObservedData"][number];

export interface ObservedDataDrawerProps {
  open: boolean;
  onClose: () => void;
  /**
   * A resolved, not-yet-adopted pair the caller should prefill the
   * expectation dialog for. Only the identity ids are passed — the caller
   * (the grid page) already has, or can look up, the display names.
   */
  onAdopt: (prefill: { tenantId: string; wasteManifestVendorId: string }) => void;
}

function renderDate(value: string | null | undefined): string {
  return value ? prettyPrintDateFull(value) : "—";
}

/**
 * `coveredThrough` ("Data through" below) is a pure calendar date built at
 * UTC midnight, unlike `firstReceivedAt`/`lastReceivedAt`, which are genuine
 * instants. Luxon's default local-zone parse would render it a day early for
 * any US viewer and disagree with the grid's own "Data through" column and
 * the CSV export — pin both zones to UTC so this drawer agrees with them.
 */
function renderCoveredThroughDate(value: string | null | undefined): string {
  return value ? prettyPrintDateFull(value, { inputZone: "utc", outputZone: "utc" }) : "—";
}

/** Any raw spelling in the group resolves to the same normalized key (the
 * scan's own grouping already guarantees this — see the resolver), so the
 * first is always a safe, complete representative for aliasing or display. */
function primaryObservedName(pair: ObservedPair): string {
  const [first] = pair.observedNames;
  return first ?? pair.observedNames.join(", ");
}

function pairKey(pair: ObservedPair): string {
  return `${pair.tenantId}::${pair.vendorId ?? primaryObservedName(pair)}`;
}

interface ObservedPairTableProps {
  pairs: ObservedPair[];
  emptyMessage: string;
  renderActions: (pair: ObservedPair) => React.ReactNode;
}

function ObservedPairTable({ pairs, emptyMessage, renderActions }: ObservedPairTableProps) {
  if (pairs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Organization</TableCell>
            <TableCell>Observed name(s)</TableCell>
            <TableCell align="right">Manifests</TableCell>
            <TableCell>First seen</TableCell>
            <TableCell>Last received</TableCell>
            <TableCell>Data through</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pairs.map((pair) => (
            <TableRow key={pairKey(pair)} data-observed-pair>
              <TableCell>{pair.tenantName}</TableCell>
              <TableCell>{pair.observedNames.join(", ")}</TableCell>
              <TableCell align="right">{pair.manifestCount}</TableCell>
              <TableCell>{renderDate(pair.firstReceivedAt)}</TableCell>
              <TableCell>{renderDate(pair.lastReceivedAt)}</TableCell>
              <TableCell>{renderCoveredThroughDate(pair.coveredThrough)}</TableCell>
              <TableCell>{renderActions(pair)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * "Scan observed vendor data" drawer — the only place in this feature that
 * touches the 15M-row WasteManifest table. Because that query is expensive
 * (~500ms+, full historical scan), it must never fire before the drawer is
 * actually opened: `skip: !open` gates it, and `ObservedDataDrawer.test.tsx`
 * pins this down as the load-bearing guard for the whole report's
 * performance story. If that gate is ever removed, the query starts running
 * on every page load of the grid.
 *
 * Results are read-only and display-only: nothing here is persisted except
 * the explicit per-pair actions (Adopt / Map to vendor / Create vendor), and
 * even those don't touch this scan's own output — they create or reuse
 * canonical vendor/alias rows and (for Adopt) hand off to the grid's own
 * expectation dialog.
 */
export function ObservedDataDrawer({ open, onClose, onAdopt }: ObservedDataDrawerProps) {
  const {
    data,
    loading,
    error,
    refetch: refetchScan,
  } = useQuery(WASTE_VENDOR_OBSERVED_DATA_QUERY, {
    skip: !open,
    // This is an explicit "scan again" action, not a cached view — staff may
    // adopt/map/create vendors while the drawer is open and expect the next
    // look to reflect it, so always hit the server rather than reuse a
    // cache-first result from an earlier open.
    fetchPolicy: "network-only",
  });

  const [mappingPair, setMappingPair] = useState<ObservedPair | null>(null);
  const [creatingPair, setCreatingPair] = useState<ObservedPair | null>(null);
  const [newVendorName, setNewVendorName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const [createVendor, { loading: creatingVendor }] = useMutation(CREATE_WASTE_MANIFEST_VENDOR);
  // The alias, not the vendor, is what changes the grid behind this drawer: a
  // canonical vendor with no alias and no expectation produces no row at all,
  // whereas aliasing makes an observed spelling roll up under the real vendor.
  // So the refetch belongs here — and only here, or the Create-vendor path would
  // fire two. The expensive `wasteVendorObservedData` scan stays explicit
  // (`refetchScan` below); this only refreshes the cheap grid query.
  const [createAlias] = useMutation(CREATE_WASTE_MANIFEST_VENDOR_ALIAS, {
    refetchQueries: [VENDOR_DATA_COMPLETENESS_QUERY],
  });

  const pairs = data?.wasteVendorObservedData ?? [];
  const resolvedPairs = pairs.filter((pair) => pair.vendorId !== null);
  const unresolvedPairs = pairs.filter((pair) => pair.vendorId === null);

  const handleAdopt = (pair: ObservedPair) => {
    if (!pair.vendorId) return;
    onAdopt({ tenantId: pair.tenantId, wasteManifestVendorId: pair.vendorId });
  };

  const openCreateVendorDialog = (pair: ObservedPair) => {
    setCreatingPair(pair);
    setNewVendorName(primaryObservedName(pair));
    setCreateError(null);
  };

  const closeCreateVendorDialog = () => {
    setCreatingPair(null);
    setNewVendorName("");
    setCreateError(null);
  };

  const handleCreateVendor = async () => {
    if (!creatingPair) return;
    const trimmedName = newVendorName.trim();
    const observedSpelling = primaryObservedName(creatingPair);

    try {
      const { data: createdData } = await createVendor({
        variables: { input: { name: trimmedName } },
      });
      const newVendorId = createdData?.createWasteManifestVendor.id;
      if (!newVendorId) {
        throw new Error("The vendor was created but no id was returned.");
      }

      // Aliasing the exact raw spelling — even when it equals the chosen
      // canonical name — is what makes this observed pair's history attach
      // to the new vendor; the scan's own grouping guarantees every other
      // raw spelling in this pair shares the same normalized key, so one
      // alias covers the whole group (see `primaryObservedName`).
      if (normalizeVendorKey(observedSpelling).length > 0) {
        await createAlias({
          variables: {
            input: { wasteManifestVendorId: newVendorId, alias: observedSpelling },
          },
        });
      }

      closeCreateVendorDialog();
      await refetchScan();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <RightDrawerShell
      open={open}
      onClose={onClose}
      title="Scan observed vendor data"
      width={{ sm: 840, md: 1000 }}
    >
      <Stack spacing={3}>
        <Alert severity="info">
          This scans every historical waste manifest for (organization, vendor) pairs that predate
          delivery tracking. It can take several seconds and never saves anything on its own — use
          Adopt, Map to vendor, or Create vendor below to act on a result.
        </Alert>

        {error && <Alert severity="error">{error.message}</Alert>}

        {loading && !data && (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {data && (
          <>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Resolved to a known vendor
              </Typography>
              <ObservedPairTable
                pairs={resolvedPairs}
                emptyMessage="No observed pairs resolved to a known vendor."
                renderActions={(pair) =>
                  pair.hasExpectation ? (
                    <StatusChip color="default" label="Already tracked" />
                  ) : (
                    <Button size="small" variant="outlined" onClick={() => handleAdopt(pair)}>
                      Adopt
                    </Button>
                  )
                }
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Not yet mapped to a vendor
              </Typography>
              <ObservedPairTable
                pairs={unresolvedPairs}
                emptyMessage="No unmapped observed vendor spellings found."
                renderActions={(pair) => (
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => setMappingPair(pair)}>
                      Map to vendor
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openCreateVendorDialog(pair)}
                    >
                      Create vendor
                    </Button>
                  </Stack>
                )}
              />
            </Box>
          </>
        )}
      </Stack>

      {mappingPair && (
        <MapVendorDialog
          open
          onClose={() => setMappingPair(null)}
          observedName={primaryObservedName(mappingPair)}
          onMapped={() => {
            setMappingPair(null);
            void refetchScan();
          }}
        />
      )}

      {creatingPair && (
        <Dialog
          open
          onClose={closeCreateVendorDialog}
          maxWidth="sm"
          fullWidth
          title="Create vendor"
          primaryActionButtonProps={{
            onClick: handleCreateVendor,
            disabled: newVendorName.trim().length < 2 || creatingVendor,
            loading: creatingVendor,
            saveText: "Create",
          }}
        >
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Observed name"
              value={creatingPair.observedNames.join(", ")}
              disabled
              fullWidth
            />
            <TextField
              label="Vendor name"
              value={newVendorName}
              onChange={(event) => setNewVendorName(event.target.value)}
              fullWidth
              inputProps={{ autoFocus: true }}
            />
            {createError && (
              <Typography variant="body2" color="error">
                {createError}
              </Typography>
            )}
          </Stack>
        </Dialog>
      )}
    </RightDrawerShell>
  );
}
